import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { secureSetItem, secureGetItem, readUnsignedValue } from '@/utils/secure-storage';

type Theme = 'light' | 'dark';

export interface ThemeContextValue {
  readonly theme: Theme;
  readonly toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'theme';

/** Synchronous read for initial render (avoids theme flash). */
function getInitialTheme(): Theme {
  const stored = readUnsignedValue(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

interface ThemeProviderProps {
  readonly children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  // Verify stored theme signature on mount; reset if tampered
  useEffect(() => {
    const verifyStored = async () => {
      try {
        const verified = await secureGetItem(localStorage, STORAGE_KEY);
        if (verified === null) {
          // No valid signed value -- re-sign the current theme
          await secureSetItem(localStorage, STORAGE_KEY, theme);
        }
      } catch {
        // Crypto/IDB unavailable -- silently continue
      }
    };
    verifyStored();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);

    const persistTheme = async () => {
      try {
        await secureSetItem(localStorage, STORAGE_KEY, theme);
      } catch {
        // Storage unavailable
      }
    };
    persistTheme();

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        theme === 'dark' ? '#09090b' : '#111827',
      );
    }
  }, [theme]);

  // Follow system preference changes when user has no explicit choice
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = async (e: MediaQueryListEvent) => {
      try {
        const stored = await secureGetItem(localStorage, STORAGE_KEY);
        if (!stored) {
          setTheme(e.matches ? 'dark' : 'light');
        }
      } catch {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
